import { useState, useEffect } from "react";
import { T, TYPES, TYPE_ORDER, FOCUS } from "./todoTokens";

const LABEL = { fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: T.ink2 };
const INPUT = `w-full h-9 rounded-[7px] px-2.5 text-[13px] mt-1.5 ${FOCUS}`;
const EDGE = { border: "1px solid #C9C3B6", color: T.ink };

const blank = { text: "", type: "action", owner: "", due_date: "", client: "", status: "open" };

/* Directive §11 — right-hand drawer. Each line of the textarea is a point;
   line 1 is the title. */
export const TodoDrawer = ({ open, editing, onClose, onSave, reps, clients, myName }) => {
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setF({
        text: (editing.points && editing.points.length ? editing.points : [editing.title]).join("\n"),
        type: editing.type || "action",
        owner: (editing.assignee || {}).name || "",
        due_date: editing.due_date || "",
        client: editing.link_type === "client" ? editing.link_id || "" : "",
        status: editing.status || "open",
      });
    } else {
      setF({ ...blank, owner: myName || "" });
    }
  }, [open, editing, myName]);

  if (!open) return null;
  const points = f.text.split("\n").map((s) => s.trim()).filter(Boolean);

  const save = async () => {
    setSaving(true);
    try { await onSave({ ...f, points }); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70]" data-testid="todo-drawer">
      <div className="absolute inset-0" style={{ background: "rgba(18,18,18,0.28)" }} onClick={onClose} />
      <div className="absolute top-0 right-0 h-full bg-white flex flex-col"
        style={{ width: "min(440px, 100vw)" }}
        onKeyDown={(e) => e.key === "Escape" && onClose()}>
        <div className="px-6 py-4" style={{ borderBottom: `1px solid ${T.rule}` }}>
          <div className="text-[18px] font-bold" style={{ fontFamily: "Manrope, sans-serif", color: T.ink }}>
            {editing ? "Edit to-do" : "Add a to-do"}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <div style={LABEL}>TO-DO</div>
            <textarea value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} rows={3} autoFocus
              placeholder="What needs doing? Put each separate point on its own line."
              className={`${INPUT} h-auto py-2`} style={EDGE} data-testid="todo-drawer-text" />
            {points.length > 1 && (
              <div className="text-[12px] mt-1" style={{ color: T.muted }}>
                Line 1 becomes the title; the other {points.length - 1} show behind “+{points.length - 1} more points”.
              </div>
            )}
          </div>

          <div>
            <div style={LABEL}>TYPE</div>
            <div className="mt-1.5 flex rounded-[7px] overflow-hidden flex-wrap" style={{ border: "1px solid #C9C3B6" }}>
              {TYPE_ORDER.filter((k) => k !== "health_check").map((k) => (
                <button key={k} onClick={() => setF({ ...f, type: k })} data-testid={`todo-drawer-type-${k}`}
                  className="flex-1 h-9 text-[12.5px] font-semibold transition-colors"
                  style={f.type === k ? { background: T.accentInk, color: "#fff" } : { background: "#fff", color: T.ink2 }}>
                  {TYPES[k].label}
                </button>
              ))}
            </div>
            {editing?.type === "health_check" && (
              <div className="text-[12px] mt-1" style={{ color: T.muted }}>
                Health checks come from the client's check cadence and can't be retyped here.
              </div>
            )}
          </div>

          <div>
            <div style={LABEL}>ASSIGNEE</div>
            <select value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })}
              className={INPUT} style={EDGE} data-testid="todo-drawer-owner">
              <option value="">Unassigned</option>
              {reps.map((r) => <option key={r} value={r}>{r === myName ? `${r} (you)` : r}</option>)}
              <option value="CLIENT_CONTACT">Client contact</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div style={LABEL}>DUE DATE</div>
              <input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })}
                className={INPUT} style={EDGE} data-testid="todo-drawer-due" />
              {editing?.type === "health_check" && (
                <div className="text-[12px] mt-1" style={{ color: T.muted }}>
                  Moves the client's next check date too.
                </div>
              )}
            </div>
            <div>
              <div style={LABEL}>STATUS</div>
              <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}
                className={INPUT} style={EDGE} data-testid="todo-drawer-status">
                <option value="open">Open</option>
                <option value="in_progress">In progress</option>
                <option value="done">Done</option>
              </select>
            </div>
          </div>

          <div>
            <div style={LABEL}>CLIENT <span style={{ fontWeight: 400, letterSpacing: 0 }}>optional</span></div>
            <select value={f.client} onChange={(e) => setF({ ...f, client: e.target.value })}
              className={INPUT} style={EDGE} data-testid="todo-drawer-client">
              <option value="">None</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {editing?.source_label && (
            <div className="text-[12.5px]" style={{ color: T.muted }}>Source: {editing.source_label}</div>
          )}
        </div>

        <div className="px-6 py-4 flex items-center justify-end gap-2" style={{ borderTop: `1px solid ${T.rule}` }}>
          <button onClick={onClose} className="h-9 px-3 text-[13px]" style={{ color: T.ink2 }}
            data-testid="todo-drawer-cancel">Cancel</button>
          <button onClick={save} disabled={!points.length || saving} data-testid="todo-drawer-save"
            className="h-9 px-4 rounded-[7px] text-[12.5px] font-bold disabled:opacity-60"
            style={{ background: points.length ? T.accent : "#E6E2D9", border: `1px solid ${T.accentEdge}`, color: T.accentInk }}>
            {saving ? "Saving…" : editing ? "Save changes" : "Add to-do"}
          </button>
        </div>
      </div>
    </div>
  );
};
